import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '../../../assets/icons/close-circle.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit-pencil.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/plus.svg';
import { ReactComponent as RevenueManagementIcon } from '../../../assets/icons/sidebar/revenue-sharing-icon.svg';
import { ReactComponent as TickIcon } from '../../../assets/icons/tick-circle-icon.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import { ManagementAccountInterFace } from '../../../interface/revenue-management.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  fetchAllAccounts,
  updateSingleAccount,
  setSortData,
  updateRevenueManagementAccountFilters,
  fetchRevenueManagementAccountFilters,
  disableRevenueManagementAccountFilterApplied,
  refreshAllAccounts,
} from '../../../redux/slices/RevenueManagementAccountSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import SuccessNotification from '../../../shared-components/success-notification/SuccessNotification';
import FacilityDetailsSidePanel from '../../../shared-components/table-side-panel/FacilityDetailsSidePanel';
import { useTableSidePanel } from '../../../shared-components/table-side-panel/hooks/useTableSidePanel';
import SidePaneModel from '../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDateTime from '../../../shared-functions/ConvertUTCtoDateTime';
import { getDaysBetween } from '../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import revenueApi from '../../../utils/api/revenue';

import AddAccountRevenueShare from './AddAccountRevenueShare';
import RevenueManagementToggle from './RevenueManageToggle';

interface ModalState {
  status: boolean;
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

interface ManagementAccountRevenueProperties {
  currentPage: string;
  setCurrentPage: React.Dispatch<React.SetStateAction<string>>;
}

interface EditPercentageProperties {
  id: string;
  value: number | string;
  accountCustomId: string;
}

/**
 * ManagementAccountRevenue page function component
 * @param {any} properties - The properties
 * @returns {any} The rendered component
 */
function ManagementAccountRevenue(properties: ManagementAccountRevenueProperties) {
  const { t, i18n } = useTranslation('revenue');
  const { currentPage, setCurrentPage } = properties;

  const dispatch = useAppDispatch();
  const inputReference = useRef<HTMLSpanElement>(null);
  const {
    accounts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filter,
    filters,
    filterListLoading,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.revenueAccount);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);

  const [editPercent, setEditPercent] = useState<EditPercentageProperties>({
    id: '',
    value: 0,
    accountCustomId: '',
  });

  const [modalView, setModalView] = useState<ModalState>({
    status: false,
    title: '',
    header: true,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const [addRevenueShareOpen, setAddRevenueShareOpen] = useState<boolean>(false);

  /**
   * Modal box close function
   */
  const closeModal = () => {
    setModalView({
      status: false,
      title: '',
      header: true,
      content: <div />,
      rightCorner: '',
      notification: false,
    });
  };

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchAccountsData = (data: any) => {
    if (getDaysBetween(data.startDate, data.endDate) > 30) {
      dispatch(
        setNotificationMessage({
          message: t('dateRangeLimitError'),
          status: true,
          type: 'error',
          code: 400,
        }),
      );
      return;
    }
    if (!loading) {
      dispatch(fetchAllAccounts(data));
    }
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchAccountsData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  const { isEditMode, handleEditModeChange, handleSidePanelClose, renderSidePanelTopLeftContent } =
    useTableSidePanel({
      onRefresh: handleRefresh,
      sessions: accounts,
    });

  /**
   * Function to handle edit percentage
   * @param {any} rowData - The rowData
   */
  const handleEditPercentage = (rowData: ManagementAccountInterFace) => {
    setEditPercent({
      id: rowData.id,
      value: rowData.revenuePercentage ? rowData.revenuePercentage : 0,
      accountCustomId: rowData.accountCustomId,
    });
  };

  /**
   * API call to update percentage
   */
  const handleUpdateRevenuePercentage = async () => {
    const savedPercentage = editPercent.value === '' ? 0 : Number(editPercent.value);
    const cpoPercentage = Math.max(0, 100 - savedPercentage);
    await revenueApi
      .updateRevenueConfigurationPercentage({
        partnerRevenueSharePercentage: savedPercentage,
        cpoRevenueSharePercentage: cpoPercentage,
        accountCustomId: editPercent.accountCustomId,
      })
      .then(() => {
        const updatedAccount: ManagementAccountInterFace | undefined = accounts.find(
          (account) => account.id === editPercent.id,
        );
        if (updatedAccount) {
          dispatch(
            updateSingleAccount({
              ...updatedAccount,
              revenuePercentage: savedPercentage,
              cpoRevenuePercentage: cpoPercentage,
            }),
          );
        }
        setEditPercent({
          id: '',
          value: 0,
          accountCustomId: '',
        });
        setModalView({
          status: true,
          title: '',
          header: false,
          rightCorner: '',
          notification: true,
          content: (
            <SuccessNotification
              title={t('partnerRevenueUpdated')}
              description=""
              notificationCloseTimer={3000}
              onNotificationClose={closeModal}
            />
          ),
        });
      })
      .catch((error) => {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Function to close the add revenue share side panel, optionally showing a
   * success modal and refreshing the table when a save just completed
   * @param {string} [successMessage] - The success message to show, if the panel closed after a successful save
   */
  const handleCloseAddRevenueShare = (successMessage?: string) => {
    setAddRevenueShareOpen(false);
    if (successMessage) {
      handleRefresh();
      setModalView({
        status: true,
        title: '',
        header: false,
        rightCorner: '',
        notification: true,
        content: (
          <SuccessNotification
            title={successMessage}
            description=""
            notificationCloseTimer={3000}
            onNotificationClose={closeModal}
          />
        ),
      });
    }
  };

  /**
   * Function to discard the in-progress percentage edit without saving
   */
  const handleCancelEditPercentage = () => {
    setEditPercent({
      id: '',
      value: 0,
      accountCustomId: '',
    });
  };

  /**
   * Function to discard the in-progress edit when clicking outside the input field
   * @param {MouseEvent} event - The mouse event
   */
  const handleOutsideClick = (event: MouseEvent) => {
    const target = event.target as Node;
    const clickedActionButtons = (target as HTMLElement).closest?.('.action-button-groups');
    if (
      editPercent.id !== '' &&
      inputReference.current &&
      !inputReference.current.contains(target) &&
      !clickedActionButtons
    ) {
      handleCancelEditPercentage();
    }
  };

  /**
   * Function to handle percentage edit, clamping to 0-100 while preserving decimals.
   * Keeps the field empty while the user is actively clearing it instead of
   * snapping back to 0, so typing a fresh value doesn't concatenate onto a stale "0".
   * @param {string} value - The percentage value
   */
  const handleChangePercentage = (value: string) => {
    if (value === '') {
      setEditPercent({
        ...editPercent,
        value: '',
      });
      return;
    }
    const percentage = Math.min(100, Math.max(0, Number(value) || 0));
    setEditPercent({
      ...editPercent,
      value: percentage,
    });
  };

  /**
   * Handle the auto refresh of the table
   */
  const handleAutoRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    dispatch(
      refreshAllAccounts({
        ...queryParameters,
        page,
        limit,
        sortBy,
        sortField,
      }),
    );
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof ManagementAccountInterFace,
      sortAllow: true,
      queryParamsKey: 'accountCustomId',
      queryParamsValue: 'accountCustomId',
      sidePanelComponent: (
        <FacilityDetailsSidePanel
          actionActive
          isEditMode={isEditMode}
          onEditModeChange={handleEditModeChange}
          fetchIdFrom="queryParams"
          onSuccess={handleRefresh}
          onRefreshList={handleAutoRefresh}
        />
      ),
      disableNewTab: true,
    },
    {
      label: t('partnerRevenuePercentage'),
      accessorKey: 'revenuePercentage' as keyof ManagementAccountInterFace,
      sortAllow: true,
      /**
       * Renders the editable partner revenue percentage cell
       * @param {ManagementAccountInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementAccountInterFace) => (
        <div className="table-row-data">
          {editPercent.id === rowData.id ? (
            <span
              role="presentation"
              className="table-percentage-input"
              ref={inputReference}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleUpdateRevenuePercentage();
                } else if (event.key === 'Escape') {
                  handleCancelEditPercentage();
                }
              }}>
              <CustomInput
                type="number"
                name="revenuePercentage"
                placeholder="0"
                value={editPercent.value}
                onChangeValue={(_name, inputValue) => handleChangePercentage(String(inputValue))}
                maxValue={100}
                decimalLimit={2}
                autoFocus
                fontSize="small"
              />
            </span>
          ) : (
            <span>{Number(rowData.revenuePercentage ?? 0).toFixed(2)}</span>
          )}
        </div>
      ),
    },
    {
      label: t('cpoRevenuePercentage'),
      accessorKey: 'revenuePercentage' as keyof ManagementAccountInterFace,
      sortAllow: true,
      /**
       * Renders the read-only CPO revenue percentage cell as the balance of the partner share
       * @param {ManagementAccountInterFace} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementAccountInterFace) => {
        const partnerPercentage =
          editPercent.id === rowData.id ? editPercent.value : (rowData.revenuePercentage ?? 0);
        const cpoPercentage = Math.max(0, 100 - Number(partnerPercentage));

        return (
          <div className="table-row-data">
            <span>{cpoPercentage.toFixed(2)}</span>
          </div>
        );
      },
    },
    {
      label: t('action'),
      accessorKey: '' as keyof ManagementAccountInterFace,
      sortAllow: false,
      sticky: 'right' as const,
      /**
       * Function for action buttons
       * @param {any} rowData - The row data
       * @returns {any} The rendered component
       */
      customData: (rowData: ManagementAccountInterFace) =>
        editPercent.id === rowData.id ? (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleUpdateRevenuePercentage()}
              role="presentation">
              <TickIcon />
            </div>
            <div
              className="action-button-items delete-color"
              onClick={() => handleCancelEditPercentage()}
              role="presentation">
              <CloseIcon />
            </div>
          </div>
        ) : (
          <div className="action-button-groups">
            <div
              className="action-button-items view-color"
              onClick={() => handleEditPercentage(rowData)}
              role="presentation">
              <EditIcon />
            </div>
          </div>
        ),
      columnWidth: 60,
    },
  ];

  /**
   * Column defination for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('accountID'), accessorKey: 'accountCustomId' },
    { label: t('partnerRevenuePercentage'), accessorKey: 'revenuePercentage' },
    { label: t('cpoRevenuePercentage'), accessorKey: 'cpoRevenuePercentage' },
    { label: t('createdAt'), accessorKey: 'createdAt' },
    { label: t('updatedAt'), accessorKey: 'updatedAt' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Table pagination function
   * @param {any} value - The value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchAccountsData({ ...filter, ...queryParameters, ...value, sortField, sortBy });
  };
  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    setExportLoader(true);
    const payload = { type: 'Partner', ...queryParameters };
    if (sortBy) {
      payload.sortBy = sortBy;
      payload.sortField = sortField;
    }
    revenueApi
      .fetchRevenueConfigurationListExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: ManagementAccountInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'accountCustomId':
                return row.accountCustomId || 'N/A';

              case 'accountName':
                return row.accountName || 'N/A';

              case 'revenuePercentage':
                return `${ToFixedValue(row.partnerRevenuePercentage) ?? 0}`;

              case 'cpoRevenuePercentage':
                return `${ToFixedValue(row.cpoRevenuePercentage) ?? 0}`;

              case 'createdAt':
                return ConvertUTCtoDateTime(row.createdAt || '-');
              case 'updatedAt':
                return ConvertUTCtoDateTime(row.updatedAt || '-');

              default:
                return (row as any)[col.accessorKey] || 'N/A';
            }
          }),
        );
        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'revenue-management-account-list.csv');
          } else {
            exportPDF(headers, rows, 'revenue-management-account-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchAccountsData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);

    // Cleanup event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [editPercent]);

  useEffect(() => {
    if (isFilterUpdated || accounts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchAccountsData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableRevenueManagementAccountFilterApplied());
    }
  }, [filters]);

  useEffect(() => {
    if (editPercent.id && inputReference.current) {
      inputReference.current.focus(); // Ensure the input is focused after state update
    }
  }, [editPercent.id]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('revenueManagement'),
        icon: <RevenueManagementIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  useEffect(() => {
    if (searchParameters.get('accountCustomId')) {
      const parameters = new URLSearchParams(searchParameters);
      parameters.delete('accountCustomId');
      setSearchParameters(parameters, { replace: true });
    }
  }, []);

  return (
    <div className="table-content" dir={i18n.dir()}>
      <ModalBox
        open={modalView.status}
        title={modalView.title}
        content={modalView.content}
        closeModal={closeModal}
        header={modalView.header}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <SidePaneModel
        open={addRevenueShareOpen}
        title=""
        hideHeader
        closePanel={() => setAddRevenueShareOpen(false)}
        content={
          <AddAccountRevenueShare
            closeModal={handleCloseAddRevenueShare}
            open={addRevenueShareOpen}
          />
        }
      />
      <CustomTable
        header={column}
        data={accounts}
        serialNo
        created
        lastUpdated
        version="v2"
        loading={loading}
        page={page}
        limit={limit}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        filters={filters}
        filtersToDispatch={updateRevenueManagementAccountFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchRevenueManagementAccountFilters}
        sizeChangeButtonRequired
        rightTopContent={
          <div className="right-action">
            <RevenueManagementToggle currentPage={currentPage} setCurrentPage={setCurrentPage} />
            <MyButton
              svgIcon={<PlusIcon />}
              label="Revenue Share"
              buttonType="primary"
              onClickFunc={() => setAddRevenueShareOpen(true)}
            />
          </div>
        }
        height={290}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        tableHeaderVersion="v1"
        exportLoader={exportLoader}
        exportDataPdfOrCSV={permissionCheck('cpoRevenueExport') ? exportDataPdfOrCSV : undefined}
        refreshRequired
        refershCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
        hoverLabel={t('refresh')}
        sidePanelTopLeftContent={renderSidePanelTopLeftContent() ?? undefined}
        onSidePanelClose={() => {
          handleSidePanelClose();
        }}
      />
    </div>
  );
}

export default ManagementAccountRevenue;
